import { WorkloadClient } from '@internal/workload-api-helper';
import { ApiValidation, ValidationResult } from '../types';

export const isValueDefined = (value?: any) =>
  value !== undefined && value !== null;

export const isOkayResponse = (response?: Response) => response?.ok === true;

export const objectIsEmpty = (object: Object) =>
  !object || Object.keys(object).length === 0;

export const isResponseCodeAcceptable = (responseCode?: number) =>
  responseCode !== undefined && responseCode > 100 && responseCode < 205;

export async function validateQuestion(
  fieldName: string,
  fieldValue: string,
  apiValidation: ApiValidation,
  workloadClient: WorkloadClient,
  nonGovernanceValidation?: boolean,
  isReadyForApiValidation?: boolean,
): Promise<string | true> {
  if (isReadyForApiValidation === false) {
    return true;
  }

  let contextValues = {};
  if (nonGovernanceValidation) {
    contextValues = {
      ...apiValidation.form.getValues(),
    };
  } else {
    contextValues = {
      ...apiValidation.form.getValues(),
      governance_group_membership: apiValidation.form
        .getValues()
        .governance_group_membership?.map((obj: { value: any }) => obj.value),
    };
  }

  const payload = {
    field_name: fieldName,
    field_value: fieldValue,
    context: contextValues,
  };

  const response = await workloadClient.apiFetch(
    'workloads/validation',
    'POST',
    payload,
  );

  if (response.status === 400) {
    const data: Partial<ValidationResult> = await response.json();
    const failures = data?.failures ?? [];

    if (failures.length > 0) {
      let errorMessage = '';
      failures.forEach(failure => {
        failure.reasons.forEach(reason => (errorMessage += `${reason}. `));
      });
      return errorMessage;
    }

    // A 400 carrying no field failures means the API rejected the request itself rather
    // than the value -- most commonly because `fieldName` is not registered in the
    // accounts-api FieldValidators enum, which responds
    // `{"message": "<field> is not a valid account request field", "failures": []}`.
    // Returning true here would report an unvalidated field as valid, so a new tag could
    // ship looking validated while accepting anything. Fail closed instead.
    return 'An error occurred calling the validation API';
  } else if (response.status >= 400) {
    return 'An error occurred calling the validation API';
  }
  return true;
}

export const REMOTE_VALIDATION_DEBOUNCE_MS = 500;

type PendingValidation = {
  timer: ReturnType<typeof setTimeout>;
  waiters: Array<(result: string | true) => void>;
};

const pendingValidations = new Map<string, PendingValidation>();

/**
 * Like `validateQuestion`, but collapses a burst of calls for the same field into a
 * single remote request made after the user pauses. Every caller in the burst receives
 * the result for the latest value, so forms that validate on change do not POST once
 * per keystroke.
 */
export function validateQuestionDebounced(
  ...args: Parameters<typeof validateQuestion>
): Promise<string | true> {
  const [fieldName] = args;
  return new Promise(resolve => {
    const previous = pendingValidations.get(fieldName);
    if (previous) {
      clearTimeout(previous.timer);
    }
    const waiters = [...(previous?.waiters ?? []), resolve];
    const timer = setTimeout(async () => {
      pendingValidations.delete(fieldName);
      const result = await validateQuestion(...args);
      waiters.forEach(waiter => waiter(result));
    }, REMOTE_VALIDATION_DEBOUNCE_MS);
    pendingValidations.set(fieldName, { timer, waiters });
  });
}
